import { can } from '@af/shared';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/ui/brand-logo';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/dropdown';
import {
  IconBell,
  IconBook,
  IconBuilding,
  IconChart,
  IconFlag,
  IconHome,
  IconKeyboard,
  IconPlay,
  IconRecord,
  IconSettings,
  IconSkill,
  IconSparkle,
  IconTasks,
} from '@/components/ui/icons';
import { CountBadge } from '@/components/ui/misc';
import { Switch } from '@/components/ui/switch';
import { ThemeSwitch } from '@/components/ui/theme-switch';
import { usd } from '@/lib/format';
import { canCreateCompany, useRole } from '@/lib/permissions';
import { useCompanies, useMe } from '@/lib/queries';
import { useTheme } from '@/lib/theme';
import { cn, IS_MOCK } from '@/lib/utils';
import { type NavItem, PRIMARY, WORKSPACE } from './nav';
import { useSwitchOrg } from './UserMenu';

type IconC = (p: { size?: number; className?: string }) => ReactNode;
export const NAV_ICONS: Record<NavItem['to'], IconC> = {
  '/': IconHome,
  '/chat': IconBuilding,
  '/master': IconSparkle,
  '/tasks': IconTasks,
  '/runs': IconPlay,
  '/flows': IconRecord,
  '/skills': IconSkill,
  '/approvals': IconFlag,
  '/usage': IconChart,
  '/run-analysis': IconChart,
  '/notifications': IconBell,
  '/help': IconBook,
  '/settings': IconSettings,
};

/** Nav row: 40px, 18px line icon, label; the active row gets a mint rule on the left. */
const rowCls =
  'group flex h-10 items-center gap-3 rounded-md px-3 text-[14px] text-fg-2 transition-colors hover:bg-hover hover:text-fg';
const rowActive = 'bg-rail-active font-medium text-fg shadow-[inset_2px_0_0_var(--rail-mark)]';

function NavRows({
  items,
  counts,
  onNavigate,
  label,
}: {
  items: NavItem[];
  counts: { approvals?: number; notifications?: number };
  onNavigate?: () => void;
  label?: string;
}) {
  const { t } = useTranslation();
  const role = useRole();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <ul className="flex flex-col gap-px" aria-label={label}>
      {items
        .filter((n) => !n.perm || can(role, n.perm))
        .map((n) => {
          const Icon = NAV_ICONS[n.to];
          return (
            <li key={n.to}>
              <Link
                to={n.to}
                onClick={onNavigate}
                // The Company chat is part of the chats page.
                className={cn(rowCls, n.to === '/chat' && pathname === '/master' && rowActive)}
                activeProps={{ className: rowActive, 'aria-current': 'page' }}
                activeOptions={{ exact: n.to === '/' }}
              >
                <Icon size={18} className="shrink-0 text-muted group-hover:text-fg" />
                <span className="flex-1 truncate">{t(n.key)}</span>
                {n.badge ? (
                  <CountBadge n={counts[n.badge]} tone={n.badge === 'approvals' ? 'warn' : 'neutral'} />
                ) : null}
              </Link>
            </li>
          );
        })}
    </ul>
  );
}

const sectionCls = 'px-3 pt-5 pb-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-muted';

function OrgCard({ spendToday, activeRuns }: { spendToday?: number; activeRuns?: number }) {
  const { t } = useTranslation();
  const me = useMe();
  const orgs = useCompanies();
  const org = me.data?.organization;
  const switchOrg = useSwitchOrg(org?.id);
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <div className="rounded-lg border border-border bg-panel p-2.5">
      <div className="flex items-center gap-2.5">
        <Logo size={26} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-medium text-fg">{org?.name ?? me.data?.user.name}</div>
          <div className="truncate text-xs text-muted">
            <span className="capitalize">{me.data?.role ?? ''}</span>
            <span className="tabular"> · {usd(spendToday ?? 0, 2)}</span>
            {activeRuns ? <span className="text-claude"> · {activeRuns} ●</span> : null}
          </div>
        </div>
        <Menu>
          <MenuTrigger className="inline-flex h-7 shrink-0 items-center rounded-md px-2 text-xs font-medium text-muted underline-offset-2 hover:bg-hover hover:text-fg hover:underline">
            {t('nav.switch')}
          </MenuTrigger>
          <MenuContent side="top" align="end">
            <MenuLabel>{t('shell.switchOrg')}</MenuLabel>
            <MenuRadioGroup value={org?.id ?? ''} onValueChange={(v) => void switchOrg(v)}>
              {(orgs.data ?? (org ? [org] : [])).map((o) => (
                <MenuRadioItem key={o.id} value={o.id}>
                  {o.name}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
            <MenuSeparator />
            <MenuItem onSelect={() => void navigate({ to: '/companies' })}>{t('companies.all')}</MenuItem>
            {canCreateCompany(me.data) ? (
              <MenuItem onSelect={() => void navigate({ to: '/companies', search: { new: true } })}>
                {t('companies.new')}
              </MenuItem>
            ) : null}
          </MenuContent>
        </Menu>
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border pt-2.5">
        <span className="text-xs text-muted">{t('theme.label')}</span>
        <ThemeSwitch />
      </div>
      {theme.theme === 'light' ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-muted">{t('theme.terminalDark')}</span>
          <Switch
            aria-label={t('theme.terminalDark')}
            checked={theme.terminalDark}
            onCheckedChange={(c) => theme.setTerminalDark(c)}
          />
        </div>
      ) : null}
    </div>
  );
}

export function Sidebar({
  counts,
  spendToday,
  activeRuns,
  onNavigate,
  onShortcuts,
  className,
}: {
  counts: { approvals?: number; notifications?: number };
  spendToday?: number;
  activeRuns?: number;
  onNavigate?: () => void;
  onShortcuts?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t('nav.main')}
      className={cn(
        'rail-scope flex h-full w-[264px] shrink-0 flex-col border-r border-border bg-rail',
        className,
      )}
    >
      <div className="flex h-14 shrink-0 items-center gap-2 px-5">
        <Link to="/" onClick={onNavigate} className="flex min-w-0 items-center gap-2 rounded-md">
          <Logo size={28} />
          <span className="truncate text-[17px] font-semibold tracking-tight text-fg">Mandistry</span>
        </Link>
        {IS_MOCK ? (
          <span className="rounded-xs border border-accent/40 px-1 font-mono text-2xs text-accent">
            {t('shell.mockMode')}
          </span>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        <NavRows items={PRIMARY} counts={counts} onNavigate={onNavigate} />

        <h2 className={sectionCls}>{t('nav.workspace')}</h2>
        <NavRows items={WORKSPACE} counts={counts} onNavigate={onNavigate} label={t('nav.workspace')} />
      </div>
      <div className="shrink-0 border-t border-border px-3 pt-2 pb-3">
        <div className="mb-2 flex items-center gap-1">
          <Link
            to="/settings"
            onClick={onNavigate}
            className={cn(rowCls, 'flex-1')}
            activeProps={{ className: rowActive, 'aria-current': 'page' }}
          >
            <IconSettings size={18} className="shrink-0 text-muted group-hover:text-fg" />
            <span className="flex-1 truncate">{t('nav.settings')}</span>
          </Link>
          {onShortcuts ? (
            <button
              type="button"
              onClick={onShortcuts}
              aria-label={t('nav.shortcuts')}
              title={`${t('nav.shortcuts')} (?)`}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-hover hover:text-fg"
            >
              <IconKeyboard size={18} />
            </button>
          ) : null}
        </div>
        <OrgCard spendToday={spendToday} activeRuns={activeRuns} />
      </div>
    </nav>
  );
}
